import { updateTaskStatus } from "@/models/tasks.model";
import { AppContext } from "@/services/AppContext/appContext";
import { genericFailureToast } from "@/utils/utils";
import { useContext } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";

const useChangeStatus = (cache: "UserTasks" | "PrecacheTasks") => {
  const { initialGetUserTasks, getPrecacheTasks, getFullTask, stopLocalTimer } =
    useContext(AppContext);
  const { taskID } = useParams();

  const completeTask = async (completedTaskID: number) => {
    const res = await updateTaskStatus({
      taskID: completedTaskID,
      status: "complete",
    });
    if (res.rStatus === "success") {
      if (cache === "UserTasks") {
        await initialGetUserTasks({ force: true });
      } else {
        await getPrecacheTasks(false, true);
      }

      await undoAlert(completedTaskID, true);

      // If the task is the one being viewed, refetch the task
      if (taskID === completedTaskID.toString()) {
        await getFullTask({ taskID: Number(taskID) });
      }

      await stopLocalTimer(completedTaskID);
    } else {
      genericFailureToast("Error completing task");
    }
  };

  const unCompleteTask = async (completedTaskID: number) => {
    const res = await updateTaskStatus({
      taskID: completedTaskID,
      status: "incomplete",
    });
    if (res.rStatus === "success") {
      if (cache === "UserTasks") {
        await initialGetUserTasks({ force: true });
      } else {
        await getPrecacheTasks(false, true);
      }
      await undoAlert(completedTaskID, false);

      // If the task is the one being viewed, refetch the task
      if (taskID === completedTaskID.toString()) {
        await getFullTask({ taskID: Number(taskID) });
      }
    } else {
      genericFailureToast("Error uncompleting task");
    }
  };

  async function undoAlert(undoTaskID: number, completed: boolean) {
    toast.success(completed ? "Task Completed!" : "Task Uncompleted!", {
      action: {
        label: "Undo",
        onClick: async () => {
          await updateTaskStatus({
            taskID: undoTaskID,
            status: completed ? "incomplete" : "complete",
          }).then(async (res) => {
            if (res.rStatus === "success") {
              // Fetches all tasks
              if (cache === "UserTasks") {
                await initialGetUserTasks({ force: true });
              } else {
                await getPrecacheTasks(false, true);
              }
            } else {
              genericFailureToast("Error undoing task");
            }
          });
        },
      },
    });
  }

  return { completeTask, unCompleteTask };
};

export default useChangeStatus;
